import React, { useCallback, useState } from "react";
import axios from "axios";
import { IntegrationConnectionErrorCode } from "@common/contracts/integrationConnection.contract";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { useEntitiesById } from "@hooks/useEntities";
import { useIntegrationConnectionRemovalMutation } from "@api/integrationConnection.api";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { CaptureClicks } from "@design-system/CaptureClicks/CaptureClicks";
import { HStack } from "@design-system/HStack/HStack";
import { Menu } from "@design-system/Menu/Menu.lib";
import { AlertIcon } from "@icons";
import { FixIntegrationConnection } from "@components/integrationConnection/FixIntegrationConnection";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { getMigrationDeleteToastMessage } from "@api/migrationDeleteFeedback";
import css from "./DisplayAccountingIntegration.module.scss";

function isAccountingMigrationExistsError(error: unknown): boolean {
    return (
        axios.isAxiosError(error) &&
        error.response?.status === 409 &&
        error.response.data?.errorCode ===
            IntegrationConnectionErrorCode.ACCOUNTING_MIGRATION_EXISTS
    );
}

interface Props {
    integrationConnection: IntegrationConnection;
}

export const DisplayAccountingIntegration: React.FC<Props> = ({
    integrationConnection,
}) => {
    const entitiesById = useEntitiesById();
    const entity = integrationConnection.entityId
        ? entitiesById.get(integrationConnection.entityId)
        : undefined;

    const [showMigrationModal, setShowMigrationModal] = useState(false);
    const { error: errorToast } = useToaster();
    const removal = useIntegrationConnectionRemovalMutation(
        integrationConnection.id,
    );

    const isDisconnected = Boolean(integrationConnection.error);

    const handleRemove = useCallback(async () => {
        try {
            await removal.mutateAsync({});
        } catch (error) {
            if (isAccountingMigrationExistsError(error)) {
                setShowMigrationModal(true);
                return;
            }
            throw error;
        }
    }, [removal]);

    const handleMigrationDecision = useCallback(
        async (removeAccountingMigration: boolean) => {
            try {
                await removal.mutateAsync({ removeAccountingMigration });
                setShowMigrationModal(false);
            } catch (error) {
                setShowMigrationModal(false);
                if (removeAccountingMigration) {
                    errorToast(
                        getMigrationDeleteToastMessage(error, {
                            statusesWithServerMessage: [403, 409],
                        }),
                    );
                    return;
                }
                throw error;
            }
        },
        [errorToast, removal],
    );

    return (
        <div className={css.container}>
            <div className={css.info}>
                {entity && <EntityAvatar entity={entity} size="xs" />}
                <span>{integrationConnection.label}</span>
            </div>

            <HStack gap={12} align="center">
                {isDisconnected && (
                    <Tooltip content="This integration has been disconnected.">
                        <span>
                            <FixIntegrationConnection
                                integrationConnection={integrationConnection}
                                variant="secondary"
                                size="sm"
                                className="broken-connection-link danger"
                                leftIcon={
                                    <AlertIcon className="icon-color-red-500" />
                                }
                            >
                                Fix connection
                            </FixIntegrationConnection>
                        </span>
                    </Tooltip>
                )}

                <CaptureClicks>
                    <Menu.More className={css.actions}>
                        <Menu.Item onClick={handleRemove} destructive>
                            Remove integration
                        </Menu.Item>
                    </Menu.More>
                </CaptureClicks>
            </HStack>

            <ConfirmationModal
                show={showMigrationModal}
                onHide={() => setShowMigrationModal(false)}
                title="Remove accounting integration"
                question="This integration has migrated accounting data. Would you like to remove the migrated data or keep it?"
                yes="Remove data"
                no="Keep data"
                onConfirm={() => handleMigrationDecision(true)}
                onReject={() => handleMigrationDecision(false)}
            />
        </div>
    );
};
